import React, { useRef } from 'react';
import { Paperclip, Plus, X } from 'lucide-react';
import { classNames } from '../../utils/classNames';
import { useI18n } from '../../hooks/useI18n';

export const AttachmentComposer = ({
  attachments = [],
  attachmentCount,
  onAddFiles,
  onRemove,
  disabled = false,
  uploading = false,
  maxCount = 5,
  variant = 'default'
}) => {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const count = attachmentCount ?? attachments.length;
  const canAddMore = count < maxCount;
  const isPill = variant === 'pill';

  return (
    <div className={classNames(isPill ? '' : 'space-y-2')}>
      {!isPill && attachments.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {attachments.map((item) => (
            <div
              key={item.id || item.url || item.previewUrl}
              className="relative h-14 w-14 overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-800"
            >
              <img src={item.previewUrl || item.url} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                disabled={disabled || uploading}
                onClick={() => onRemove?.(item)}
                className="absolute end-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white"
                aria-label={t('removeAttachment')}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files || []);
          if (files.length) {
            onAddFiles?.(files);
          }
          event.target.value = '';
        }}
      />

      <button
        type="button"
        disabled={disabled || uploading || !canAddMore}
        onClick={() => inputRef.current?.click()}
        className={classNames(
          'inline-flex items-center justify-center transition disabled:opacity-40',
          isPill
            ? 'mb-0.5 h-9 w-9 rounded-full text-neutral-500 hover:bg-neutral-200/70 hover:text-neutral-800'
            : 'gap-1.5 rounded-lg border border-neutral-200 px-2.5 py-1.5 text-xs text-neutral-600 hover:bg-neutral-50 dark:border-neutral-800 dark:text-neutral-300'
        )}
        title={t('attachFiles')}
      >
        {isPill ? <Plus size={18} /> : <Paperclip size={13} />}
        {isPill ? null : (
          <>
            {uploading ? t('uploadingAttachments') : t('attachFiles')}
            <span className="text-neutral-400">
              {count}/{maxCount}
            </span>
          </>
        )}
      </button>
    </div>
  );
};
